import { useTranslation } from 'react-i18next'
import type { Entry } from 'contentful'
import styled from 'styled-components'

import type { IPageStoriesFields, IPageStoryFields } from 'contentful-shared'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { useBreakpoints } from 'bl-common/src/hooks/useBreakpoints'
import { Appear } from 'bl-common/src/units/Appear'
import { Section } from 'bl-common/src/units/Section/Section'
import { mediaObj } from 'bl-common/src/utils/media'

import { Metadata } from '../../Metadata'
import Page from '../../Page'
import NewsletterSignupFormSection from '../../TemplatePage/sections/NewsletterSignupFormSection'
import { BackToStoriesLink } from './BackToStoriesLink'
import { StoryCard } from './StoryCard'

const ContentWrap = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridGap: `${theme.spacing[6]} ${theme.spacing[2]}`,

  [mediaObj.sm]: {
    gridTemplateColumns: '1fr 1fr',
  },
  [mediaObj.md]: {
    gridTemplateColumns: '1fr 1fr 1fr',
  },
  [mediaObj.mlg]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
  },
}))

type AllStoriesPageProps = {
  page: IPageStoriesFields
  stories: Entry<IPageStoryFields>[]
}

const AllStoriesPage = ({ page, stories }: AllStoriesPageProps) => {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoints()
  const { newsletterSignup } = page

  return (
    <Page breadcrumb={t('allStories')}>
      <Metadata title={t('allStories')} description={page.subtitle} />
      <Section as="header" top={{ xs: 2 }}>
        <BackToStoriesLink top={{ xs: 2.5 }} bottom={{ xs: 2, md: 4 }} />
        <Type preset="headlineExtraLarge" as="h1" case="capitalize">
          {t('allStories')}
        </Type>
      </Section>
      {stories?.length > 0 && (
        <Section top={{ xs: 0 }}>
          <Appear>
            <ContentWrap>
              {stories?.map(story => (
                <StoryCard
                  key={story.sys.id}
                  imageRatio={isMobile ? 0.9 : 1.3}
                  title={story.fields.title}
                  topic={story.fields.topic}
                  date={story.fields.publishDate}
                  image={story.fields.heroImage.fields.image}
                  link={story.fields.slug}
                />
              ))}
            </ContentWrap>
          </Appear>
        </Section>
      )}
      {newsletterSignup && (
        <NewsletterSignupFormSection section={newsletterSignup} />
      )}
    </Page>
  )
}

export default AllStoriesPage
